import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ProposalPage() {
  const [proposals, setProposals] = useState([]);
  const [selected, setSelected] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [msg, setMsg] = useState('');
  const [quotes, setQuotes] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [form, setForm] = useState({ quote_id: '', prospect_id: '', project_name: '', client_name: '', notes: '', language: 'ES' });
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState('');

  const fetchAll = useCallback(async () => {
    const [pRes, qRes, prRes] = await Promise.all([
      fetch(`${API}/api/proposals`, { credentials: 'include' }),
      fetch(`${API}/api/quotes`, { credentials: 'include' }),
      fetch(`${API}/api/b2b/prospects`, { credentials: 'include' })
    ]);
    if (pRes.ok) setProposals(await pRes.json());
    if (qRes.ok) { const d = await qRes.json(); setQuotes(Array.isArray(d) ? d : d.quotes || []); }
    if (prRes.ok) { const d = await prRes.json(); setProspects(Array.isArray(d) ? d : d.prospects || []); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const showMsg = (t) => { setMsg(t); setTimeout(() => setMsg(''), 5000); };

  const handleGenerate = async () => {
    if (!form.project_name && !form.quote_id && !form.prospect_id) { showMsg('Ingrese nombre de proyecto o seleccione cotizacion/prospecto'); return; }
    setGenerating(true);
    try {
      const res = await fetch(`${API}/api/proposals/generate`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (res.ok) {
        const data = await res.json();
        setSelected(data);
        showMsg('Propuesta generada exitosamente');
        fetchAll();
      } else { const e = await res.json(); showMsg(e.detail || 'Error'); }
    } catch (e) { showMsg('Error de conexion'); }
    setGenerating(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar esta propuesta?')) return;
    await fetch(`${API}/api/proposals/${id}`, { method: 'DELETE', credentials: 'include' });
    if (selected?.id === id) setSelected(null);
    fetchAll();
  };

  const handleSaveEdit = async () => {
    if (!selected) return;
    const res = await fetch(`${API}/api/proposals/${selected.id}`, {
      method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content: editContent })
    });
    if (res.ok) {
      setSelected({ ...selected, content: editContent });
      setEditing(false);
      showMsg('Propuesta guardada');
    }
  };

  const handleStatusChange = async (id, status) => {
    await fetch(`${API}/api/proposals/${id}`, {
      method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status })
    });
    fetchAll();
    if (selected?.id === id) setSelected({ ...selected, status });
  };

  const loadFull = async (id) => {
    const res = await fetch(`${API}/api/proposals/${id}`, { credentials: 'include' });
    if (res.ok) setSelected(await res.json());
  };

  const statusColors = { draft: 'bg-slate-600', sent: 'bg-blue-600', approved: 'bg-green-600', rejected: 'bg-red-600' };
  const statusLabels = { draft: 'Borrador', sent: 'Enviada', approved: 'Aprobada', rejected: 'Rechazada' };

  return (
    <div data-testid="proposal-page" className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white">Proposal Writer IA</h1>
          <p className="text-slate-400 text-sm">Genera propuestas narrativas profesionales para licitaciones B2B con GPT-4o</p>
        </div>
      </div>
      {msg && <div className="bg-blue-900/30 border border-blue-700 text-blue-200 px-4 py-2 rounded text-sm">{msg}</div>}

      <Tabs defaultValue="generate">
        <TabsList>
          <TabsTrigger value="generate" data-testid="tab-generate">Generar Propuesta</TabsTrigger>
          <TabsTrigger value="list" data-testid="tab-proposals-list">Propuestas ({proposals.length})</TabsTrigger>
          {selected && <TabsTrigger value="view" data-testid="tab-view">Ver Propuesta</TabsTrigger>}
        </TabsList>

        {/* GENERATE TAB */}
        <TabsContent value="generate" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="bg-slate-900/60 border-slate-700">
              <CardHeader><CardTitle className="text-white text-base">Datos del Proyecto</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-slate-400 text-xs block mb-1">Nombre del Proyecto *</label>
                  <input data-testid="proposal-project-name" className="w-full bg-slate-800 text-white border border-slate-600 rounded px-3 py-2 text-sm"
                    placeholder="Ej: Instalacion CCTV - Edificio Comercial Miami"
                    value={form.project_name} onChange={e => setForm(f => ({ ...f, project_name: e.target.value }))} />
                </div>
                <div>
                  <label className="text-slate-400 text-xs block mb-1">Cliente</label>
                  <input className="w-full bg-slate-800 text-white border border-slate-600 rounded px-3 py-2 text-sm"
                    placeholder="Nombre del cliente o empresa"
                    value={form.client_name} onChange={e => setForm(f => ({ ...f, client_name: e.target.value }))} />
                </div>
                <div>
                  <label className="text-slate-400 text-xs block mb-1">Basado en Cotizacion (opcional)</label>
                  <select data-testid="proposal-quote-select" className="w-full bg-slate-800 text-white border border-slate-600 rounded px-3 py-2 text-sm"
                    value={form.quote_id} onChange={e => setForm(f => ({ ...f, quote_id: e.target.value }))}>
                    <option value="">-- Sin cotizacion --</option>
                    {quotes.map(q => <option key={q.id} value={q.id}>#{q.quote_number} - {q.client_name} (${q.total?.toLocaleString()})</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-slate-400 text-xs block mb-1">Basado en Prospecto B2B (opcional)</label>
                  <select className="w-full bg-slate-800 text-white border border-slate-600 rounded px-3 py-2 text-sm"
                    value={form.prospect_id} onChange={e => setForm(f => ({ ...f, prospect_id: e.target.value }))}>
                    <option value="">-- Sin prospecto --</option>
                    {prospects.map(p => <option key={p.id} value={p.id}>{p.company || p.company_name} - {p.contact_name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-slate-400 text-xs block mb-1">Notas Adicionales</label>
                  <textarea className="w-full bg-slate-800 text-white border border-slate-600 rounded px-3 py-2 text-sm" rows={3}
                    placeholder="Detalles especificos, requisitos del GC, etc."
                    value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
                </div>
                <div className="flex gap-3 items-end">
                  <div className="flex-1">
                    <label className="text-slate-400 text-xs block mb-1">Idioma</label>
                    <select className="w-full bg-slate-800 text-white border border-slate-600 rounded px-3 py-2 text-sm"
                      value={form.language} onChange={e => setForm(f => ({ ...f, language: e.target.value }))}>
                      <option value="ES">Espanol</option>
                      <option value="EN">English</option>
                    </select>
                  </div>
                  <Button data-testid="generate-proposal-btn" onClick={handleGenerate} disabled={generating}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-6">
                    {generating ? 'Generando...' : 'Generar Propuesta'}
                  </Button>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/60 border-slate-700">
              <CardHeader><CardTitle className="text-white text-base">Estructura de la Propuesta</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {['Portada', 'Carta de Presentacion', 'Sobre IQES LLC', 'Alcance del Trabajo (SOW)', 'Metodologia', 'Cronograma Estimado', 'Inversion (Desglose de Costos)', 'Garantias y Soporte', 'Terminos y Condiciones', 'Por Que Elegirnos', 'Siguiente Paso'].map((s, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <span className="w-6 h-6 bg-indigo-900/50 text-indigo-300 rounded-full flex items-center justify-center font-bold">{i + 1}</span>
                      <span className="text-slate-300">{s}</span>
                    </div>
                  ))}
                </div>
                {generating && (
                  <div className="mt-4 flex items-center gap-3">
                    <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                    <p className="text-indigo-300 text-sm">Generando propuesta con GPT-4o... (30-60 seg)</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* LIST TAB */}
        <TabsContent value="list" className="mt-4">
          <div className="space-y-2">
            {proposals.length === 0 ? (
              <p className="text-slate-500 text-center py-8">Sin propuestas. Genere una nueva.</p>
            ) : proposals.map(p => (
              <div key={p.id} className="bg-slate-800/60 border border-slate-700 rounded-lg p-4 flex items-center justify-between cursor-pointer hover:bg-slate-700/60"
                onClick={() => loadFull(p.id)}>
                <div>
                  <p className="text-white font-medium">{p.project_name}</p>
                  <p className="text-slate-400 text-xs">{p.client_name} | {new Date(p.created_at).toLocaleDateString('es-ES')} | {p.created_by}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`${statusColors[p.status] || 'bg-slate-600'} text-white text-xs px-2 py-0.5 rounded`}>{statusLabels[p.status] || p.status}</span>
                  <Button variant="outline" size="sm" className="text-red-400 border-red-600 text-xs" onClick={e => { e.stopPropagation(); handleDelete(p.id); }}>X</Button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* VIEW TAB */}
        {selected && (
          <TabsContent value="view" className="mt-4">
            <Card className="bg-slate-900/60 border-slate-700">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-white text-lg">{selected.project_name}</CardTitle>
                    <p className="text-slate-400 text-xs mt-1">{selected.client_name} | {selected.language === 'ES' ? 'Espanol' : 'English'} | {new Date(selected.created_at).toLocaleDateString('es-ES')}</p>
                  </div>
                  <div className="flex gap-2">
                    {['draft', 'sent', 'approved', 'rejected'].map(s => (
                      <Button key={s} size="sm" variant={selected.status === s ? 'default' : 'outline'}
                        className={`text-xs ${selected.status === s ? statusColors[s] + ' text-white' : ''}`}
                        onClick={() => handleStatusChange(selected.id, s)}>
                        {statusLabels[s]}
                      </Button>
                    ))}
                    <Button size="sm" variant="outline" onClick={() => { setEditing(!editing); setEditContent(selected.content || ''); }}>
                      {editing ? 'Cancelar' : 'Editar'}
                    </Button>
                    {editing && <Button size="sm" className="bg-green-600 text-white" onClick={handleSaveEdit}>Guardar</Button>}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {editing ? (
                  <textarea className="w-full bg-slate-800 text-white border border-slate-600 rounded px-4 py-3 text-sm font-mono" rows={30}
                    value={editContent} onChange={e => setEditContent(e.target.value)} />
                ) : (
                  <div data-testid="proposal-content" className="bg-slate-800/60 rounded-lg p-6 text-slate-200 text-sm leading-relaxed whitespace-pre-wrap max-h-[600px] overflow-y-auto">
                    {selected.content || 'Sin contenido'}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
